import React from 'react';
import { Bell, Check, ExternalLink, Info, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { useUserNotifications, UserNotification } from '../context/UserNotificationsContext';

interface NotificationDropdownProps {
    onClose: () => void;
    onViewAll: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ onClose, onViewAll }) => {
    const { notifications, markAsRead, markAllAsRead, isLoading } = useUserNotifications();

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return <CheckCircle2 className="text-emerald-500" size={18} />;
            case 'warning': return <AlertTriangle className="text-amber-500" size={18} />;
            case 'sos': return <Bell className="text-rose-500 animate-ring" size={18} />;
            case 'system': return <Info className="text-sky-500" size={18} />;
            default: return <Info className="text-slate-400" size={18} />;
        }
    };

    const recentNotifications = notifications.slice(0, 5);

    return (
        <div className="absolute right-0 top-full mt-4 w-80 sm:w-96 bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-100 overflow-hidden z-[1000] animate-in slide-in-from-top-4 duration-300">
            {/* Header */}
            <div className="p-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
                <div>
                    <h3 className="text-sm font-black uppercase italic tracking-tighter text-slate-800 flex items-center gap-2">
                        <Bell size={14} className="text-[#FF6B00]" /> Notificaciones
                    </h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Novedades y Alertas</p>
                </div>
                <div className="flex items-center gap-2">
                    <button 
                        onClick={() => markAllAsRead()}
                        className="p-2 hover:bg-emerald-50 text-slate-400 hover:text-emerald-500 rounded-xl transition-colors group"
                        title="Marcar todas como leídas"
                    >
                        <Check size={16} />
                    </button>
                    <button 
                        onClick={onClose}
                        className="lg:hidden p-2 hover:bg-slate-100 text-slate-400 rounded-xl transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="max-h-[400px] overflow-y-auto no-scrollbar">
                {isLoading && notifications.length === 0 ? (
                    <div className="p-12 text-center">
                        <div className="w-10 h-10 border-4 border-[#FF6B00]/20 border-t-[#FF6B00] rounded-full animate-spin mx-auto"></div>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-12 text-center space-y-3">
                        <div className="w-16 h-16 bg-slate-50 rounded-[1.5rem] flex items-center justify-center text-slate-200 mx-auto">
                            <Bell size={32} />
                        </div>
                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest italic">Sin novedades aún</p>
                    </div>
                ) : (
                    <div className="divide-y divide-slate-50">
                        {recentNotifications.map((n) => (
                            <div 
                                key={n.id} 
                                onClick={() => !n.is_read && markAsRead(n.id)}
                                className={`p-5 hover:bg-slate-50 transition-colors cursor-pointer group relative ${!n.is_read ? 'bg-orange-50/30' : ''}`}
                            >
                                <div className="flex gap-4">
                                    <div className="mt-1 shrink-0">{getIcon(n.type)}</div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <h4 className={`text-xs font-black uppercase tracking-tighter truncate ${!n.is_read ? 'text-[#1E3A8A]' : 'text-slate-600'}`}>
                                                {n.title}
                                            </h4>
                                            <span className="text-[9px] text-slate-300 font-bold whitespace-nowrap">
                                                {new Date(n.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-1 line-clamp-2">
                                            {n.message}
                                        </p>
                                    </div>
                                </div>
                                {!n.is_read && (
                                    <div className="absolute left-0 top-0 w-1 h-full bg-[#FF6B00]"></div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer */}
            <button 
                onClick={onViewAll}
                className="w-full p-4 text-center bg-slate-50/50 hover:bg-slate-100/50 border-t border-slate-50 transition-all group"
            >
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 group-hover:text-[#FF6B00] flex items-center justify-center gap-2">
                    Ver todo el panel <ExternalLink size={12} />
                </span>
            </button>
            
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes ring {
                    0% { transform: rotate(0); }
                    10% { transform: rotate(15deg); }
                    20% { transform: rotate(-10deg); }
                    30% { transform: rotate(5deg); }
                    40% { transform: rotate(-5deg); }
                    50% { transform: rotate(0); }
                    100% { transform: rotate(0); }
                }
                .animate-ring {
                    animation: ring 2s infinite;
                }
            ` }} />
        </div>
    );
};
